import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card, Button } from '@medikei/ui';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { ArrowLeft, Menu, X, Home, ChevronRight } from 'lucide-react';
import { cn } from '@/utils/cn';

const landingNavLinks = [
  { label: 'Como funciona', href: '/#como-funciona' },
  { label: 'Especialidades', href: '/#especialidades' },
  { label: 'Consulta domiciliar', href: '/#domiciliar' },
  { label: 'Indique e ganhe', href: '/#indique' },
];

interface AuthLayoutProps {
  children: React.ReactNode;
  logo?: boolean;
  showHeader?: boolean;
  cardClassName?: string;
}

export function AuthLayout({
  children,
  logo = false,
  showHeader = true,
  cardClassName,
}: AuthLayoutProps) {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isLoginPage = location.pathname === '/login';
  const isRegisterPage = location.pathname === '/register';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary/5 via-background to-background text-foreground">
      {showHeader && (
        <header
          className={cn(
            'sticky top-0 z-50 w-full transition-all duration-300 border-b',
            isScrolled
              ? 'bg-white/90 dark:bg-neutral-900/90 backdrop-blur-md shadow-sm border-border/80'
              : 'bg-white/70 dark:bg-neutral-900/70 backdrop-blur-md border-border/50'
          )}
        >
          <div className="container-landing">
            <div className="flex items-center justify-between h-16 lg:h-20">
              {/* Logo & Home Shortcut */}
              <div className="flex items-center gap-3">
                <Link
                  to="/"
                  className="flex items-center group transition-transform duration-200 hover:scale-[1.02]"
                  aria-label="Voltar para a página inicial"
                >
                  <img
                    src="/logo.png"
                    alt="Medikei"
                    className="h-9 lg:h-11 w-auto drop-shadow-sm"
                    fetchPriority="high"
                    decoding="async"
                  />
                </Link>

                <Link
                  to="/"
                  className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-muted/70 hover:bg-muted text-muted-foreground hover:text-foreground transition-all ml-2 border border-border/60"
                  title="Voltar para a Landing Page"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  <span>Início</span>
                </Link>
              </div>

              {/* Desktop Nav Links */}
              <nav className="hidden md:flex items-center gap-6 lg:gap-8">
                {landingNavLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors"
                  >
                    {link.label}
                  </a>
                ))}
              </nav>

              {/* Desktop Actions */}
              <div className="hidden md:flex items-center gap-3">
                <ThemeToggle />

                {isLoginPage ? (
                  <Link to="/register">
                    <Button variant="primary" size="sm" className="rounded-xl font-semibold shadow-sm">
                      Cadastre-se
                    </Button>
                  </Link>
                ) : isRegisterPage ? (
                  <Link to="/login">
                    <Button variant="outline" size="sm" className="rounded-xl font-semibold">
                      Fazer login
                    </Button>
                  </Link>
                ) : (
                  <Link to="/login">
                    <Button variant="outline" size="sm" className="rounded-xl font-semibold">
                      Entrar
                    </Button>
                  </Link>
                )}
              </div>

              {/* Mobile Menu & Theme Toggle */}
              <div className="flex items-center gap-2 md:hidden">
                <ThemeToggle />
                <button
                  onClick={() => setIsMobileMenuOpen(true)}
                  className="p-2 rounded-lg text-foreground hover:text-primary hover:bg-muted/50 transition-colors"
                  aria-label="Abrir menu de navegação"
                >
                  <Menu className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Mobile Drawer */}
      {isMobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="fixed top-0 right-0 bottom-0 z-[101] bg-white dark:bg-neutral-900 w-full max-w-xs border-l border-border flex flex-col p-6 shadow-2xl animate-in slide-in-from-right duration-200">
            <div className="flex items-center justify-between pb-4 border-b border-border">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center"
              >
                <img src="/logo.png" alt="Medikei" className="h-8 w-auto" decoding="async" />
              </Link>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 rounded-lg text-foreground hover:bg-muted transition-colors"
                aria-label="Fechar menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto py-6 space-y-1">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium text-foreground hover:bg-muted transition-colors"
              >
                <span className="flex items-center gap-2.5">
                  <Home className="w-4 h-4 text-primary" />
                  Página Inicial
                </span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </Link>

              {landingNavLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium text-foreground hover:bg-muted transition-colors"
                >
                  <span>{link.label}</span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </a>
              ))}
            </nav>

            <div className="pt-4 border-t border-border space-y-3">
              {isLoginPage ? (
                <Link
                  to="/register"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="w-full block"
                >
                  <Button variant="primary" size="md" className="w-full rounded-xl font-semibold">
                    Criar nova conta
                  </Button>
                </Link>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="w-full block"
                >
                  <Button variant="outline" size="md" className="w-full rounded-xl font-semibold">
                    Acessar minha conta
                  </Button>
                </Link>
              )}

              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-xs text-center text-muted-foreground hover:text-foreground flex items-center justify-center gap-1.5 py-1"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Voltar para o site principal</span>
              </Link>
            </div>
          </div>
        </>
      )}

      {/* Main Content Area */}
      <main className="flex-1 flex items-center justify-center p-4 py-8 sm:py-12">
        <div className="w-full max-w-md">
          {logo && (
            <div className="text-center mb-6">
              <Link to="/" className="inline-block group transition-transform duration-200 hover:scale-105">
                <img
                  src="/logo.png"
                  alt="Medikei"
                  className="h-11 mx-auto drop-shadow-sm"
                  decoding="async"
                />
              </Link>
            </div>
          )}
          <Card className={cn('p-6 sm:p-8 shadow-xl shadow-primary/5 border border-border/80 rounded-2xl', cardClassName)}>
            {children}
          </Card>
        </div>
      </main>
    </div>
  );
}

interface AuthHeaderProps {
  title: string;
  description?: string;
}

export function AuthHeader({ title, description }: AuthHeaderProps) {
  return (
    <div className="mb-6">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
        {title}
      </h1>
      {description && (
        <p className="text-sm text-gray-500 dark:text-neutral-400">{description}</p>
      )}
    </div>
  );
}

interface FormDividerProps {
  text?: string;
}

export function FormDivider({ text = 'ou' }: FormDividerProps) {
  return (
    <div className="relative my-6">
      <div className="absolute inset-0 flex items-center">
        <div className="w-full border-t border-gray-200 dark:border-neutral-800" />
      </div>
      <div className="relative flex justify-center">
        <span className="bg-white dark:bg-neutral-900 px-3 text-xs text-gray-500 dark:text-neutral-400 uppercase tracking-wider transition-colors">
          {text}
        </span>
      </div>
    </div>
  );
}

interface FormFooterProps {
  children: React.ReactNode;
  className?: string;
}

export function FormFooter({ children, className = '' }: FormFooterProps) {
  return (
    <p className={`text-sm text-gray-500 dark:text-neutral-400 text-center mt-6 ${className}`}>
      {children}
    </p>
  );
}
